import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, RefreshCw, Search, FileText, Landmark, Smartphone, Save } from "lucide-react";
import * as XLSX from "xlsx";
import { todayCR } from "../utils/dateHelpers";

const TYPE_BADGE_MAP = {
  SINPE: { icon: Smartphone, color: "bg-blue-100 text-blue-700 border-blue-200" },
  TRANSFERENCIA: { icon: FileText, color: "bg-purple-100 text-purple-700 border-purple-200" },
  "PAGO POR BANCO": { icon: Landmark, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const TypeBadge = ({ type }) => {
  const c = TYPE_BADGE_MAP[type] || { icon: FileText, color: "bg-slate-100 text-slate-600" };
  const I = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${c.color}`}>
      <I className="w-3 h-3" />
      {type}
    </span>
  );
};

/**
 * SinpeLog — Registro histórico agregado de SINPE + Transferencia + Pagos por Banco.
 * Auto-refresh cada 60s cuando la fecha == hoy. Exporta a Excel.
 */
export default function SinpeLog({ embedded = false }) {
  const navigate = useNavigate();
  const { selectedBox } = useBox() || {};
  const [date, setDate] = useState(todayCR());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("all"); // all|SINPE|TRANSFERENCIA|PAGO POR BANCO
  const [allBoxes, setAllBoxes] = useState(false); // Master puede ver de todas sus cajas
  const [capturing, setCapturing] = useState(false); // Buscar y guardar ahora (IMAP)
  const timerRef = useRef(null);

  const isToday = date === todayCR();

  const captureNow = async () => {
    setCapturing(true);
    try {
      const params = new URLSearchParams();
      if (selectedBox?.id && !allBoxes) params.set("box_id", selectedBox.id);
      if (allBoxes) params.set("all_boxes", "true");
      const r = await axiosInstance.post(`/sinpe-log/capture-now?${params.toString()}`);
      const d = r.data || {};
      toast.success(`Guardado: ${d.total_sinpe || 0} SINPE y ${d.total_transfers || 0} transferencias en ${d.boxes || 0} caja(s)`);
      await fetchLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo buscar/guardar los movimientos");
    } finally {
      setCapturing(false);
    }
  };

  const fetchLog = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({ date });
      if (selectedBox?.id && !allBoxes) params.set("box_id", selectedBox.id);
      const r = await axiosInstance.get(`/sinpe-log?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      toast.error("No se pudo cargar el registro");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { fetchLog(); }, [date, selectedBox?.id, allBoxes]);

  // Auto-refresh cada 60s SOLO cuando la fecha es hoy
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (isToday) {
      timerRef.current = setInterval(() => fetchLog(true), 60000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [date, isToday, selectedBox?.id]);

  const shiftDate = (days) => {
    const d = new Date(date + "T12:00:00");
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().slice(0, 10));
  };

  const filtered = (data?.entries || []).filter((e) => {
    if (filterType !== "all" && e.movement_type !== filterType) return false;
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (
      (e.customer_name || "").toLowerCase().includes(needle) ||
      (e.reference || "").toLowerCase().includes(needle) ||
      (e.description || "").toLowerCase().includes(needle) ||
      (e.customer_id || "").toLowerCase().includes(needle)
    );
  });

  const filteredTotal = filtered.reduce((s, e) => s + Number(e.amount_colones || 0), 0);

  const exportExcel = () => {
    if (!data || !data.entries?.length) { toast.error("No hay datos para exportar"); return; }
    const rows = filtered.map((e, i) => ({
      "#": i + 1,
      Hora: (e.timestamp || "").slice(11, 16),
      Fecha: (e.timestamp || "").slice(0, 10),
      Tipo: e.movement_type,
      Origen: e.source_label,
      Cliente: e.customer_name || "",
      Cédula: e.customer_id || "",
      "Monto ₡": Number(e.amount_colones || 0),
      "Monto $": Number(e.amount_dollars || 0),
      "Nº Comprobante": e.reference || "",
      Descripción: e.description || "",
      "Registrado por": e.created_by || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 4 }, { wch: 7 }, { wch: 11 }, { wch: 16 }, { wch: 18 }, { wch: 24 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 22 }, { wch: 40 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `SINPE ${data.date}`);
    // Summary row
    const summary = [
      ["Total registros:", filtered.length],
      ["Total ₡:", filteredTotal],
      ["SINPE ₡:", filtered.filter(e => e.movement_type === "SINPE").reduce((s, e) => s + Number(e.amount_colones || 0), 0)],
      ["Transferencia ₡:", filtered.filter(e => e.movement_type === "TRANSFERENCIA").reduce((s, e) => s + Number(e.amount_colones || 0), 0)],
      ["Pago por Banco ₡:", filtered.filter(e => e.movement_type === "PAGO POR BANCO").reduce((s, e) => s + Number(e.amount_colones || 0), 0)],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(summary);
    XLSX.utils.book_append_sheet(wb, ws2, "Resumen");
    XLSX.writeFile(wb, `sinpe_log_${data.date}${selectedBox?.name ? "_" + selectedBox.name.replace(/[^a-z0-9]/gi, "_") : ""}.xlsx`);
  };

  const fmt = (n) => `₡${Number(n || 0).toLocaleString("es-CR", { maximumFractionDigits: 2 })}`;
  const fmtTime = (iso) => (iso || "").slice(11, 16);

  const openLinked = (e) => {
    if (e.linked_type === "sale") navigate("/inventory");
    else if (e.linked_type === "debt") navigate("/debts");
  };

  return (
    <div className={embedded ? "" : "container mx-auto p-4 space-y-4"} data-testid="sinpe-log-page">
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold">Registro SINPE / Transferencias</h1>
            <p className="text-sm text-slate-600">Historial persistente de movimientos electrónicos por día CR · Guardado automático de SINPE y transferencias a las 11:59 PM (hora CR)</p>
          </div>
          {isToday && (
            <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded">
              <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-pulse"></span>
              Actualiza automáticamente cada 60s
            </span>
          )}
        </div>
      )}

      {/* Controles */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <Button size="sm" variant="outline" onClick={() => shiftDate(-1)} data-testid="date-prev-btn">
              <ChevronLeft className="w-4 h-4" /> Ayer
            </Button>
            <div>
              <Label className="text-xs">Fecha</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="sinpe-date-input" />
            </div>
            <Button size="sm" variant="outline" onClick={() => shiftDate(1)} disabled={date >= todayCR()} data-testid="date-next-btn">
              Mañana <ChevronRight className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDate(todayCR())} data-testid="date-today-btn">
              Hoy
            </Button>
            <Button size="sm" variant="ghost" onClick={() => fetchLog()} disabled={loading} data-testid="sinpe-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Buscar</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cliente, referencia, descripción..." className="pl-7 h-9" data-testid="sinpe-search-input" />
              </div>
            </div>
            <Button onClick={captureNow} disabled={capturing} variant="outline" className="border-blue-500 text-blue-700 hover:bg-blue-50" data-testid="sinpe-capture-now-btn">
              <Save className={`w-4 h-4 mr-1 ${capturing ? "animate-pulse" : ""}`} /> {capturing ? "Buscando..." : "Buscar y guardar ahora"}
            </Button>
            <Button onClick={exportExcel} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sinpe-export-btn">
              <Download className="w-4 h-4 mr-1" /> Exportar Excel
            </Button>
          </div>

          {/* Filtro por tipo */}
          <div className="flex gap-1 flex-wrap items-center justify-between">
            <div className="flex gap-1 flex-wrap">
              {["all", "SINPE", "TRANSFERENCIA", "PAGO POR BANCO"].map((t) => (
                <button
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={`text-xs px-3 py-1 rounded-full border transition ${filterType === t ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`sinpe-filter-${t}`}
                >
                  {t === "all" ? "Todos" : t}
                </button>
              ))}
            </div>
            <label className="text-xs flex items-center gap-1.5 text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allBoxes}
                onChange={(e) => setAllBoxes(e.target.checked)}
                data-testid="sinpe-all-boxes-check"
              />
              Ver todas las cajas
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Totales */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Card>
            <CardContent className="pt-3 pb-3 text-center">
              <div className="text-xs text-slate-500 uppercase">Total del día</div>
              <div className="text-lg font-bold text-slate-800" data-testid="sinpe-total-day">{fmt(data.total_colones)}</div>
              <div className="text-xs text-slate-500">{data.count} movimientos</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-3 pb-3 text-center">
              <div className="text-xs text-blue-700 uppercase">SINPE</div>
              <div className="text-lg font-bold text-blue-800">{fmt(data.by_type?.SINPE || 0)}</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-3 pb-3 text-center">
              <div className="text-xs text-purple-700 uppercase">Transferencia</div>
              <div className="text-lg font-bold text-purple-800">{fmt(data.by_type?.TRANSFERENCIA || 0)}</div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-3 pb-3 text-center">
              <div className="text-xs text-emerald-700 uppercase">Pago por Banco</div>
              <div className="text-lg font-bold text-emerald-800">{fmt(data.by_type?.["PAGO POR BANCO"] || 0)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabla */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex justify-between items-center">
            <span>Movimientos del {date}</span>
            {q || filterType !== "all" ? (
              <span className="text-xs font-normal text-slate-500">Filtrados: {filtered.length} · {fmt(filteredTotal)}</span>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-center py-6 text-slate-500">Cargando...</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-center py-6 text-slate-500">Sin movimientos {q || filterType !== "all" ? "que coincidan" : "para este día"}.</p>
          )}
          {!loading && filtered.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sinpe-table">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-2">Hora</th>
                    <th className="text-left px-2 py-2">Tipo</th>
                    <th className="text-left px-2 py-2">Origen</th>
                    <th className="text-left px-2 py-2">Cliente</th>
                    <th className="text-right px-2 py-2">Monto</th>
                    <th className="text-left px-2 py-2">Nº Comprobante</th>
                    <th className="text-left px-2 py-2">Descripción</th>
                    <th className="text-left px-2 py-2">Por</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <tr key={e.id} className="border-b hover:bg-blue-50/50 cursor-pointer" onClick={() => openLinked(e)} data-testid={`sinpe-row-${e.id}`}>
                      <td className="px-2 py-2 font-mono text-xs">{fmtTime(e.timestamp)}</td>
                      <td className="px-2 py-2"><TypeBadge type={e.movement_type} /></td>
                      <td className="px-2 py-2 text-xs text-slate-600">{e.source_label}</td>
                      <td className="px-2 py-2">{e.customer_name || <span className="text-slate-400 italic text-xs">—</span>}</td>
                      <td className="px-2 py-2 text-right font-semibold text-emerald-700">{fmt(e.amount_colones)}</td>
                      <td className="px-2 py-2 font-mono text-xs">{e.reference || <span className="text-slate-400 italic">—</span>}</td>
                      <td className="px-2 py-2 text-xs text-slate-600 truncate max-w-xs" title={e.description}>{e.description || "—"}</td>
                      <td className="px-2 py-2 text-xs text-slate-500">{e.created_by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-bold">
                    <td colSpan={4} className="px-2 py-2 text-right">TOTAL:</td>
                    <td className="px-2 py-2 text-right text-emerald-700">{fmt(filteredTotal)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
